import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Plus, Loader2, History } from 'lucide-react';
import { Tooltip } from '@mui/material';

import { useOrgaConfigurationContext } from '@/app/providers';
import { BuyCreditsModal } from './BuyCreditsModal';

// Helpers
const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
};

// Main Component
interface ShippingCreditsIndicatorProps {
  organizationId: string;
}

export const ShippingCreditsIndicator: React.FC<ShippingCreditsIndicatorProps> = ({
  organizationId,
}) => {
  // Use real-time configuration hook - creditsBalance is already in real-time!
  const { configuration, loading: configLoading } = useOrgaConfigurationContext();

  // State for purchase modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Get unified credits balance from real-time configuration
  // This updates automatically when credits change in Firestore!
  const creditsBalance = configuration?.creditsBalance;
  const balanceCents = creditsBalance?.balanceCents ?? 0;

  const handleOpenPurchaseModal = () => {
    setShowPurchaseModal(true);
  };

  const isLowBalance = balanceCents < 500; // Less than 5€

  return (
    <>
      <Tooltip
        title={
          <div className="py-1 px-0.5">
            <div className="font-semibold mb-1">Crédits (Expédition & IA)</div>
            <div className="text-xs opacity-80">
              Cliquez sur le solde pour voir l'historique
            </div>
          </div>
        }
        arrow
        placement="bottom"
      >
        <div className={`
          flex items-center rounded-full overflow-hidden
          ${isLowBalance
            ? 'bg-gradient-to-r from-amber-900/60 to-orange-900/60 ring-1 ring-amber-500/40'
            : 'bg-slate-800/80 ring-1 ring-slate-600/50'
          }
          shadow-lg backdrop-blur-sm
        `}>
          {/* Balance display with link */}
          <Link
            to={`/orga/${organizationId}/admin/shipping-credits`}
            className={`
              flex items-center gap-2 pl-3 pr-2.5 py-1.5
              transition-all duration-200 hover:bg-white/5
            `}
          >
            <Wallet className={`w-4 h-4 ${isLowBalance ? 'text-amber-400' : 'text-emerald-400'}`} />
            {configLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
            ) : (
              <span className={`text-sm font-semibold tabular-nums ${isLowBalance ? 'text-amber-200' : 'text-white'}`}>
                {formatCurrency(balanceCents)}
              </span>
            )}
          </Link>

          {/* History icon */}
          <Link
            to={`/orga/${organizationId}/admin/shipping-credits`}
            className={`
              flex items-center justify-center w-8 h-8 
              transition-all duration-200 hover:bg-white/5
              ${isLowBalance
                ? 'text-amber-300 hover:text-amber-200'
                : 'text-slate-300 hover:text-white'
              }
            `}
            title="Voir l'historique des crédits"
          >
            <History className="w-4 h-4" />
          </Link>

          {/* Divider */}
          <div className={`w-px h-5 ${isLowBalance ? 'bg-amber-600/40' : 'bg-slate-600/60'}`} />

          {/* Quick purchase button */}
          <button
            type="button"
            onClick={handleOpenPurchaseModal}
            className={`
              flex items-center justify-center w-8 h-8 
              transition-all duration-200
              ${isLowBalance
                ? 'text-amber-300 hover:text-amber-200 hover:bg-amber-500/20'
                : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20'
              }
            `}
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </Tooltip>

      {/* Purchase Modal */}
      <BuyCreditsModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        organizationId={organizationId}
        currentBalance={balanceCents}
      />
    </>
  );
};

export default ShippingCreditsIndicator;
